import type { DeviceStatusResponse } from '@/types/api-client/device-tools'
import { isUispOffline, isUispOnline } from './deviceHealth'
import { isCriticalSeverity, isHealthySeverity, isWarningSeverity } from './severityStatuses'

/**
 * Checks if the response indicates an unsupported NMS type
 */
const isUnsupportedNms = (result: DeviceStatusResponse): boolean => {
	return result.message?.toLowerCase().includes('unsupported nms') ?? false
}

/**
 * Gets a human-readable description for the device status
 *
 * @param result - The device status response from the API
 * @param t - The i18n translation function
 * @returns Localized description text for the status
 */
export const getStatusDescription = (
	result: DeviceStatusResponse | null,
	t: (key: string) => string
): string => {
	if (!result) {
		return t('device-tools.status.descriptions.waiting')
	}

	// Check for unsupported NMS type first
	if (isUnsupportedNms(result)) {
		return t('device-tools.status.descriptions.unsupported-nms')
	}

	// Check for offline device first
	if (result.deviceOnline === false) {
		return t('device-tools.status.descriptions.unreachable')
	}

	// Check for UISP disconnected status (deviceOnline may be misleadingly true)
	if (isUispOffline(result)) {
		return t('device-tools.status.descriptions.disconnected')
	}

	// Check for alarm status
	if (result.alarmStatus) {
		return t('device-tools.status.descriptions.issues-detected')
	}

	// Handle UISP 'n/a' severity with active status
	const severity = result.severity?.toLowerCase()
	if (severity === 'n/a' && isUispOnline(result)) {
		return t('device-tools.status.descriptions.operating-normally')
	}

	// Use centralized severity checks
	if (isHealthySeverity(result.severity)) {
		return t('device-tools.status.descriptions.operating-normally')
	}

	if (isWarningSeverity(result.severity)) {
		return t('device-tools.status.descriptions.minor-issues')
	}

	if (isCriticalSeverity(result.severity)) {
		return t('device-tools.status.descriptions.immediate-attention')
	}

	return ''
}

/**
 * Gets a concise label for the device status
 *
 * @param result - The device status response from the API
 * @param t - The i18n translation function
 * @returns Localized label text for the status (e.g., "Healthy", "Warning", "Critical")
 */
export const getStatusLabel = (
	result: DeviceStatusResponse | null,
	t: (key: string) => string
): string => {
	if (!result) {
		return t('device-tools.status.labels.waiting')
	}

	// Check for unsupported NMS type first
	if (isUnsupportedNms(result)) {
		return t('device-tools.status.labels.unsupported-nms')
	}

	// Check for offline device first
	if (result.deviceOnline === false) {
		return t('device-tools.status.labels.offline')
	}

	// Check for UISP disconnected status (deviceOnline may be misleadingly true)
	if (isUispOffline(result)) {
		return t('device-tools.status.labels.disconnected')
	}

	// Check for alarm status
	if (result.alarmStatus) {
		return t('device-tools.status.labels.alarm')
	}

	const severity = result.severity?.toLowerCase()

	// Handle default/initial severity as waiting state
	if (!severity || severity === 'default' || severity === 'initial') {
		return t('device-tools.status.labels.waiting')
	}

	// Handle UISP 'n/a' severity - check nested status for active
	if (severity === 'n/a') {
		if (isUispOnline(result)) {
			return t('device-tools.status.labels.healthy')
		}
		return t('device-tools.status.labels.unknown')
	}

	// Use centralized severity checks
	if (isHealthySeverity(severity)) {
		return t('device-tools.status.labels.healthy')
	}

	// Special case: 'info' severity without alarm is considered healthy (handled above)
	// but with alarm should show as 'online'
	if (severity === 'info' && result.alarmStatus) {
		return t('device-tools.status.labels.online')
	}

	if (isWarningSeverity(severity)) {
		return t('device-tools.status.labels.warning')
	}

	if (isCriticalSeverity(severity)) {
		return t('device-tools.status.labels.critical')
	}

	// Fallback to raw severity or unknown
	return result.severity || t('device-tools.status.labels.unknown')
}

/**
 * Gets a detailed message for the device status
 *
 * @param result - The device status response from the API
 * @param t - The i18n translation function
 * @returns Localized message text for the status
 */
export const getStatusMessage = (
	result: DeviceStatusResponse | null,
	t: (key: string) => string
): string => {
	if (!result) {
		return t('device-tools.status.messages.ready-status')
	}

	// Check for unsupported NMS type - use localized message
	if (isUnsupportedNms(result)) {
		return t('device-tools.status.messages.unsupported-nms')
	}

	// Return the message from the API, or fallback
	return result.message || t('device-tools.status.messages.fallback')
}
